import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { useSession } from '../core/session.tsx';
import { registry } from '../core/registry.ts';
import { useServices } from '../core/services.tsx';
import { useCapabilities, useOnline } from '../core/hooks.ts';
import { Badge, Button,Dialog,Notice } from '../ui/index.tsx';
import { DesktopUpdateNotice } from '../shared/DesktopUpdates.tsx';
import {useChannelScope} from '../core/channelScope.tsx';
import {useOp} from '../core/api.ts';

const GROUPS = ['Trabajo', 'Canal', 'Sistema'] as const;
const NAV = GROUPS.map((group) => ({
  group,
  items: registry
    .filter((m) => m.nav?.group === group && m.path)
    .sort((a, b) => a.nav!.order - b.nav!.order)
    .map((m) => ({ to: `/${m.path}`, label: m.nav!.label, icon: m.nav!.icon })),
})).filter((g) => g.items.length > 0);

/** Acciones deterministas primero; la interpretación libre requiere un proveedor configurado. */
const ACTIONS: { label: string; keywords: string; to: string }[] = [
  { label: 'Crear vídeo', keywords: 'crear nuevo video vídeo proyecto short largo', to: '/proyectos?nuevo=1' },
  { label: 'Abrir guiones', keywords: 'abrir guion guión escribir texto', to: '/guiones' },
  { label: 'Crear guion', keywords: 'crear nuevo guion guión escribir texto', to: '/proyectos?nuevo=1&entrada=guion' },
  { label: 'Buscar recurso', keywords: 'buscar recurso musica música sfx clip biblioteca', to: '/biblioteca' },
  { label: 'Ver agenda', keywords: 'ver agenda calendario publicar programar', to: '/calendario' },
  { label: 'Importar catálogo', keywords: 'importar manifiesto catalogo catálogo csv json', to: '/biblioteca?importar=1' },
  { label: 'Registrar incidente de derechos', keywords: 'reclamacion reclamación copyright strike incidente', to: '/derechos' },
  { label: 'Importar métricas', keywords: 'metricas métricas resultados estadisticas csv', to: '/resultados' },
  { label: 'Editar reglas de estilo', keywords: 'reglas estilo perfil perrito subtitulos', to: '/estilo' },
  { label: 'Registrar equipo', keywords: 'equipo pc dispositivo enrolar', to: '/equipos' },
  { label: 'Exportar mis datos', keywords: 'exportar privacidad datos eliminar cuenta', to: '/privacidad' },
];

function CommandBox() {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);
  const norm = (s: string) => s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
  const enabledPaths = new Set(registry.map((m) => m.path));
  const results = q.trim()
    ? ACTIONS.filter((a) => enabledPaths.has(a.to.slice(1).split(/[?/]/)[0])).filter((a) => norm(q).split(/\s+/).every((w) => norm(a.label + ' ' + a.keywords).includes(w)))
    : [];
  const go = (to: string) => {
    setQ('');
    navigate(to);
  };
  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'ArrowDown') setActive((a) => Math.min(a + 1, results.length - 1));
    if (e.key === 'ArrowUp') setActive((a) => Math.max(a - 1, 0));
    if (e.key === 'Enter' && results[active]) go(results[active].to);
    if (e.key === 'Escape') setQ('');
  };
  return (
    <div className="command">
      <label htmlFor="cmd" className="visually-hidden">¿Qué quieres hacer?</label>
      <input
        id="cmd"
        className="input"
        placeholder="¿Qué quieres hacer? (crear vídeo, buscar recurso, ver agenda…)"
        value={q}
        onChange={(e) => {
          setQ(e.target.value);
          setActive(0);
        }}
        onKeyDown={onKey}
        role="combobox"
        aria-expanded={results.length > 0}
        aria-controls="cmd-results"
        aria-autocomplete="list"
      />
      {q.trim() && (
        <ul id="cmd-results" className="command-results" role="listbox">
          {results.length === 0 && <li className="muted small" style={{ padding: 8 }}>Sin acción directa. La interpretación de texto libre necesita un proveedor de IA configurado (Conexiones).</li>}
          {results.map((r, i) => (
            <li key={r.to} role="option" aria-selected={i === active}>
              <button type="button" aria-selected={i === active} onClick={() => go(r.to)}>{r.label}</button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function useTheme() {
  const [theme, setTheme] = useState<string>(() => {
    try {
      return localStorage.getItem('estudio.theme') ?? 'system';
    } catch {
      return 'system';
    }
  });
  useEffect(() => {
    if (theme === 'system') document.documentElement.removeAttribute('data-theme');
    else document.documentElement.setAttribute('data-theme', theme);
    try {
      localStorage.setItem('estudio.theme', theme);
    } catch {
      // preferencia solo en memoria
    }
  }, [theme]);
  return [theme, setTheme] as const;
}

export function Layout() {
  const { workspace, workspaces, session, displayName, signOut, chooseWorkspace } = useSession();
  const { mode } = useServices();
  const {channelId,selectChannel}=useChannelScope();
  const channels=useOp('workspace.channel_list',{workspace_id:workspace.id});
  const online = useOnline();
  const caps = useCapabilities();
  const [open, setOpen] = useState(false);
  const [accountOpen,setAccountOpen]=useState(false),[accountError,setAccountError]=useState('');
  const [theme, setTheme] = useTheme();
  const location = useLocation();
  const mainRef = useRef<HTMLElement>(null);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <div className="app-shell">
      <a className="skip-link" href="#contenido">Saltar al contenido</a>
      <nav className={`sidebar ${open ? 'open' : ''}`} aria-label="Navegación principal">
        <div className="brand">
          <span className="brand-mark" aria-hidden="true">P</span>
          <span>PromptCut</span>
        </div>
        <div className="small muted" style={{ padding: '0 8px' }}>{workspace.name}</div>
        {workspaces.length > 1 && <Button size="s" variant="ghost" onClick={chooseWorkspace}>Cambiar espacio</Button>}
        <label className="field sidebar-channel">Canal de trabajo<select className="select" title="Filtra tus métricas, vídeos, guiones y análisis" value={channelId} onChange={e=>selectChannel(e.target.value)}><option value="">Todos los canales</option>{channels.data?.items.map(c=><option value={c.id} key={c.id}>{c.name}</option>)}</select></label>
        {NAV.map((g) => (
          <div key={g.group}>
            <div className="nav-group-title">{g.group}</div>
            {g.items.map((it) => (
              <NavLink key={it.to} to={it.to} className="nav-link">
                <span className="nav-icon" aria-hidden="true">{it.icon}</span>
                {it.label}
              </NavLink>
            ))}
          </div>
        ))}
        <div className="spacer" />
        <div className="small muted" style={{ padding: 8 }}>
          Motor local:{' '}
          {caps ? (caps.engine.available ? <Badge tone={caps.simulated ? 'sim' : 'ok'}>{caps.simulated ? 'simulado' : 'disponible'}</Badge> : <Badge>{caps.runtime === 'desktop' ? 'pendiente de integrar' : 'no disponible en web'}</Badge>) : '…'}
        </div>
      </nav>
      <div className="main">
        <DesktopUpdateNotice />
        {mode === 'demo' && <div className="banner banner-sim" role="status">Modo demostración · datos simulados, separados de cualquier cuenta real. Nada de lo que ves es un resultado real.</div>}
        {!online && <div className="banner banner-offline" role="status">Sin conexión. Los cambios de texto quedan pendientes en este equipo; aprobar, publicar y borrar requieren conexión.</div>}
        <header className="topbar">
          <Button className="menu-toggle" variant="ghost" aria-expanded={open} aria-label="Abrir menú" onClick={() => setOpen((o) => !o)}>☰</Button>
          <CommandBox />
          <div className="spacer" />
          <label className="visually-hidden" htmlFor="theme">Tema</label>
          <select id="theme" className="select" style={{ width: 'auto' }} value={theme} onChange={(e) => setTheme(e.target.value)}>
            <option value="system">Tema del sistema</option>
            <option value="light">Claro</option>
            <option value="dark">Oscuro</option>
          </select>
          <span className="small muted nowrap" title={session.email ?? ''}>{displayName}</span>
          <Button size="s" onClick={() => setAccountOpen(true)}>Cuenta y canales</Button>
        </header>
        <Dialog open={accountOpen} title="Cuenta y canales" onClose={()=>setAccountOpen(false)}><div className="stack"><p><strong>{displayName}</strong> · {session.email}</p><p>Cambia el canal de trabajo para ver otra marca usando esta misma cuenta. Para trabajar con otro correo, cierra esta sesión e inicia con la otra cuenta.</p><label className="field">Canal de trabajo<select className="select" value={channelId} onChange={e=>{selectChannel(e.target.value);setAccountOpen(false);}}><option value="">Todos los canales</option>{channels.data?.items.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}</select></label>{workspaces.length>1&&<Button onClick={()=>{setAccountOpen(false);chooseWorkspace();}}>Elegir otro espacio privado</Button>}<Button onClick={()=>{setAccountOpen(false);void signOut().catch(()=>{setAccountError('No se pudo cerrar la sesión. Inténtalo nuevamente.');setAccountOpen(true);});}}>Cerrar sesión para usar otra cuenta</Button>{accountError&&<Notice tone="danger">{accountError}</Notice>}</div></Dialog>
        <main id="contenido" ref={mainRef} className="content" tabIndex={-1}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
